// ============================================================
// Contexte d'auth dédié au Super Admin — Lot L04 du plan SaaS
// ============================================================
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import superAdminApi from '../services/superAdminApi';

const SuperAdminAuthContext = createContext(null);

// Délai d'inactivité avant fermeture automatique de la session Super Admin.
const INACTIVITE_MS = 15 * 60 * 1000; // 15 minutes

export const SuperAdminAuthProvider = ({ children }) => {
  const [superAdmin, setSuperAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('sv_sa_access_token');
    if (token) {
      superAdminApi.get('/superadmin/auth/me')
        .then(res => setSuperAdmin(res.data.superAdmin))
        .catch(() => {
          localStorage.removeItem('sv_sa_access_token');
          localStorage.removeItem('sv_sa_refresh_token');
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  // Stocke la session SA puis charge le profil complet (photo, téléphone…).
  const etablirSessionSA = async ({ accessToken, refreshToken, superAdmin: sa }) => {
    localStorage.setItem('sv_sa_access_token', accessToken);
    localStorage.setItem('sv_sa_refresh_token', refreshToken);
    setSuperAdmin(sa);
    try {
      const me = await superAdminApi.get('/superadmin/auth/me');
      if (me.data.superAdmin) { setSuperAdmin(me.data.superAdmin); return me.data.superAdmin; }
    } catch {}
    return sa;
  };

  const login = async (identifiant, password) => {
    const res = await superAdminApi.post('/superadmin/auth/login', { identifiant, password });
    // 2FA active : pas de session émise, on demande un code.
    if (res.data?.mfaRequired) {
      return { mfaRequired: true, challengeToken: res.data.challengeToken };
    }
    return etablirSessionSA(res.data);
  };

  // Deuxième étape quand la 2FA SA est active : échange challengeToken + code.
  const login2fa = async (challengeToken, code) => {
    const res = await superAdminApi.post('/superadmin/auth/login/2fa', { challengeToken, code });
    return etablirSessionSA(res.data);
  };

  const refreshSuperAdmin = async () => {
    try {
      const res = await superAdminApi.get('/superadmin/auth/me');
      setSuperAdmin(res.data.superAdmin);
      return res.data.superAdmin;
    } catch {
      return null;
    }
  };

  const logout = async () => {
    const refreshToken = localStorage.getItem('sv_sa_refresh_token');
    try {
      await superAdminApi.post('/superadmin/auth/logout', { refreshToken });
    } catch (_) {}
    localStorage.removeItem('sv_sa_access_token');
    localStorage.removeItem('sv_sa_refresh_token');
    setSuperAdmin(null);
  };

  // Référence vers logout pour l'utiliser dans l'effet sans le mettre en
  // dépendance (logout est recréé à chaque rendu).
  const logoutRef = useRef(logout);
  logoutRef.current = logout;

  // ── Time-out d'inactivité (15 min) ──────────────────────────────────────
  // Tant qu'un Super Admin est connecté, on arme un minuteur. La moindre
  // action (souris, clavier, clic, scroll, toucher) le réarme. Sans action
  // pendant 15 minutes, la session est fermée automatiquement.
  useEffect(() => {
    if (!superAdmin) return;

    let timer;
    const fermer = () => {
      // Drapeau lu par la page de connexion SA pour afficher « session expirée ».
      try { sessionStorage.setItem('sv_sa_session_expiree', '1'); } catch { /* ignore */ }
      logoutRef.current();
      toast('Session fermée après 15 minutes d\'inactivité.', { icon: '🔒', duration: 6000 });
    };
    const rearmer = () => {
      clearTimeout(timer);
      timer = setTimeout(fermer, INACTIVITE_MS);
    };

    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach((e) => window.addEventListener(e, rearmer, { passive: true }));
    rearmer(); // démarre le compte à rebours

    return () => {
      clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, rearmer));
    };
  }, [superAdmin]);

  return (
    <SuperAdminAuthContext.Provider value={{ superAdmin, loading, login, login2fa, logout, refreshSuperAdmin }}>
      {children}
    </SuperAdminAuthContext.Provider>
  );
};

export const useSuperAdminAuth = () => {
  const ctx = useContext(SuperAdminAuthContext);
  if (!ctx) throw new Error('useSuperAdminAuth doit être utilisé dans SuperAdminAuthProvider');
  return ctx;
};
